/**
 * @module components/header
 *
 * The fixed header: logo injection, scroll state, the mobile navigation panel,
 * active-section highlighting and the search trigger.
 *
 * The nav is authored in HTML so it works without JavaScript; this module only
 * adds behaviour on top.
 */

import { ScrollTrigger, registerPlugins } from "../core/gsap-setup.js";
import { qs, qsa, on, setAttributes, debounce } from "../core/dom.js";
import { NAV } from "../data/site.js";
import { logoLockup } from "../svg/logo.js";
import { hydrateIconSlots } from "../svg/icons.js";

/**
 * @param {Object} [options]
 * @param {import("../core/smooth-scroll.js").ScrollController|null} [options.scroll]
 * @returns {() => void} Teardown.
 */
export function initHeader(options = {}) {
  registerPlugins();

  const header = qs("[data-header]");
  if (!header) return () => {};

  const disposers = [];

  /* --- Logo ------------------------------------------------------------- */

  const brandSlot = qs("[data-brand]", header);
  if (brandSlot && !brandSlot.querySelector(".logo")) {
    brandSlot.innerHTML = logoLockup({ variant: "header", size: 34 });
  }

  /* --- Scroll state ----------------------------------------------------- */

  const setScrolled = (scrolled) => {
    header.classList.toggle("is-scrolled", scrolled);
  };
  setScrolled(window.scrollY > 12);

  const scrollTrigger = ScrollTrigger.create({
    start: 12,
    end: "max",
    onUpdate: (self) => setScrolled(self.scroll() > 12),
    onToggle: (self) => setScrolled(self.isActive),
  });
  disposers.push(() => scrollTrigger.kill());

  /* --- Mobile navigation ------------------------------------------------ */

  const toggle = qs("[data-nav-toggle]", header);
  const mobileNav = qs("[data-mobile-nav]", header);

  if (toggle && mobileNav) {
    const setOpen = (open) => {
      setAttributes(toggle, { "aria-expanded": String(open) });
      if (open) {
        mobileNav.removeAttribute("hidden");
      } else {
        mobileNav.setAttribute("hidden", "");
      }
    };

    disposers.push(
      on(toggle, "click", () => {
        const open = toggle.getAttribute("aria-expanded") === "true";
        setOpen(!open);
      })
    );

    // Close when a link is followed.
    disposers.push(
      on(mobileNav, "click", (event) => {
        if (event.target instanceof Element && event.target.closest("a")) setOpen(false);
      })
    );

    // Close on Escape.
    disposers.push(
      on(document, "keydown", (event) => {
        if (event.key === "Escape" && toggle.getAttribute("aria-expanded") === "true") {
          setOpen(false);
          toggle.focus();
        }
      })
    );

    // Reset when the layout grows past the breakpoint.
    const mq = window.matchMedia("(min-width: 1080px)");
    const onChange = (event) => {
      if (event.matches) setOpen(false);
    };
    mq.addEventListener("change", onChange);
    disposers.push(() => mq.removeEventListener("change", onChange));

    setOpen(false);
  }

  /* --- Active section --------------------------------------------------- */

  const links = qsa("[data-nav-link]", header);
  const mobileLinks = qsa("[data-mobile-link]", header);
  const ids = NAV.map((item) => item.id);

  const setActive = (id) => {
    [...links, ...mobileLinks].forEach((link) => {
      const target = link.getAttribute("href")?.replace("#", "");
      if (id && target === id) {
        setAttributes(link, { "aria-current": "true" });
      } else {
        link.removeAttribute("aria-current");
      }
    });
  };

  const sections = ids
    .map((id) => document.getElementById(id))
    .filter((node) => node instanceof HTMLElement);

  if (sections.length) {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible?.target?.id) setActive(visible.target.id);
      },
      { rootMargin: "-45% 0px -45% 0px", threshold: [0, 0.15, 0.4, 0.75] }
    );

    sections.forEach((section) => observer.observe(section));
    disposers.push(() => observer.disconnect());
  }

  // Refresh ScrollTrigger positions after any nav-driven layout shift.
  disposers.push(
    on(
      window,
      "resize",
      debounce(() => ScrollTrigger.refresh(), 220)
    )
  );

  /* --- Search trigger --------------------------------------------------- */

  hydrateIconSlots(header);

  return () => disposers.forEach((dispose) => dispose());
}
